import { computed } from 'vue'
import type { PetitionState } from './state'

export const createGetters = (state: PetitionState) => {
  const filteredPetitions = computed(() => {
    let petitions = [...state.petitions]

    // Search filter
    if (state.filters.search) {
      const searchLower = state.filters.search.toLowerCase()
      petitions = petitions.filter(
        (p) =>
          p.title.toLowerCase().includes(searchLower) ||
          p.description.toLowerCase().includes(searchLower)
      )
    }

    // Status filter
    if (state.filters.status) {
      petitions = petitions.filter((p) => p.status === state.filters.status)
    }

    // Category filter
    if (state.filters.category) {
      petitions = petitions.filter((p) => p.category === state.filters.category)
    }

    // Priority filter
    if (state.filters.priority) {
      petitions = petitions.filter((p) => p.priority === state.filters.priority)
    }

    // Project filter
    if (state.filters.projectId) {
      petitions = petitions.filter((p) => p.projectId === state.filters.projectId)
    }

    return petitions
  })

  const stats = computed(() => {
    const all = state.petitions
    return {
      total: all.length,
      requested: all.filter((p) => p.status === 'requested').length,
      analyzing: all.filter((p) => p.status === 'analyzing').length,
      feedback: all.filter((p) => p.status === 'feedback').length,
      costing: all.filter((p) => p.status === 'costing').length,
      presented: all.filter((p) => p.status === 'presented').length,
      approved: all.filter((p) => p.status === 'approved').length,
      rejected: all.filter((p) => p.status === 'rejected').length,
      converted: all.filter((p) => p.status === 'converted').length,
      pending: all.filter((p) =>
        ['requested', 'analyzing', 'feedback', 'costing', 'presented'].includes(p.status)
      ).length,
    }
  })

  const petitionsByStatus = computed(() => {
    const grouped: Record<string, typeof state.petitions> = {
      requested: [],
      analyzing: [],
      feedback: [],
      costing: [],
      presented: [],
      approved: [],
      rejected: [],
      converted: [],
    }

    for (const petition of state.petitions) {
      if (grouped[petition.status]) {
        grouped[petition.status].push(petition)
      }
    }

    return grouped
  })

  const petitionsByCategory = computed(() => {
    const grouped: Record<string, typeof state.petitions> = {
      new_feature: [],
      improvement: [],
      bug_report: [],
      change_request: [],
      support: [],
      other: [],
    }

    for (const petition of state.petitions) {
      if (grouped[petition.category]) {
        grouped[petition.category].push(petition)
      }
    }

    return grouped
  })

  // Get petitions ready to be converted (approved but not yet converted)
  const readyToConvert = computed(() => {
    return state.petitions.filter(
      (p) => p.status === 'approved' && !p.convertedToRequirementId
    )
  })

  return {
    filteredPetitions,
    stats,
    petitionsByStatus,
    petitionsByCategory,
    readyToConvert,
  }
}
